import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Search, Filter, Target, Clock, Award } from 'lucide-react';
import { Activity, ActivityCategory } from '../types';
import { ActivityCard, ActivityForm } from '../components/activities';
import { Button } from '../components/shared/Button';
import { Card } from '../components/shared/Card';

// Mock data
const mockActivities: Activity[] = [
  {
    id: '1',
    title: 'Varsity Soccer Team',
    category: 'sports',
    description: 'Starting midfielder for the varsity soccer team. Responsible for organizing plays and supporting both offensive and defensive strategies.',
    startDate: new Date(2023, 8, 1),
    ongoing: true,
    hoursPerWeek: 12,
    leadershipRole: 'Team Captain',
    achievements: [
      'Led team to regional championship',
      'MVP award for 2023 season',
      'Organized team fundraising events'
    ],
  },
  {
    id: '2',
    title: 'Local Food Bank Volunteer',
    category: 'volunteer',
    description: 'Weekly volunteer at the community food bank, helping with food distribution, inventory management, and customer service.',
    startDate: new Date(2023, 5, 1),
    ongoing: true,
    hoursPerWeek: 4,
    achievements: [
      'Completed 100+ volunteer hours',
      'Trained new volunteers',
      'Improved inventory system efficiency'
    ],
  },
  {
    id: '3',
    title: 'Science Club',
    category: 'academic',
    description: 'Active member of the school science club, participating in experiments, science fairs, and STEM outreach programs.',
    startDate: new Date(2023, 8, 1),
    endDate: new Date(2024, 5, 31),
    ongoing: false,
    hoursPerWeek: 3,
    leadershipRole: 'Vice President',
    achievements: [
      'Won 2nd place at regional science fair',
      'Organized elementary school STEM workshop',
    ],
  },
];

export const Activities: React.FC = () => {
  const [activities, setActivities] = useState<Activity[]>(mockActivities);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState<Activity | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ActivityCategory | 'all'>('all');

  const categories: { value: ActivityCategory | 'all'; label: string }[] = [
    { value: 'all', label: 'All Categories' },
    { value: 'sports', label: 'Sports' },
    { value: 'arts', label: 'Arts' },
    { value: 'volunteer', label: 'Volunteer' },
    { value: 'academic', label: 'Academic' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'internship', label: 'Internship' },
    { value: 'work', label: 'Work' },
    { value: 'other', label: 'Other' },
  ];

  const filteredActivities = activities.filter(activity => {
    const matchesSearch = activity.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         activity.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || activity.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleSaveActivity = (activityData: Omit<Activity, 'id'>) => {
    if (editingActivity) {
      setActivities(prev => prev.map(activity =>
        activity.id === editingActivity.id
          ? { ...activityData, id: editingActivity.id }
          : activity
      ));
    } else {
      const newActivity: Activity = {
        ...activityData,
        id: Math.random().toString(36).substr(2, 9),
      };
      setActivities(prev => [newActivity, ...prev]);
    }
    setEditingActivity(undefined);
  };

  const handleEditActivity = (activity: Activity) => {
    setEditingActivity(activity);
    setIsFormOpen(true);
  };

  const handleDeleteActivity = (id: string) => {
    if (window.confirm('Are you sure you want to delete this activity?')) {
      setActivities(prev => prev.filter(activity => activity.id !== id));
    }
  };

  const totalHours = activities.reduce((sum, activity) => {
    const weeks = activity.ongoing
      ? Math.ceil((new Date().getTime() - activity.startDate.getTime()) / (1000 * 60 * 60 * 24 * 7))
      : Math.ceil((activity.endDate!.getTime() - activity.startDate.getTime()) / (1000 * 60 * 60 * 24 * 7));
    return sum + (activity.hoursPerWeek * weeks);
  }, 0);

  const leadershipRoles = activities.filter(a => a.leadershipRole).length;

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col lg:flex-row lg:items-center lg:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Activity Tracker
          </h1>
          <p className="text-lg text-gray-600">
            Document your extracurricular activities and achievements
          </p>
        </div>

        <Button
          onClick={() => setIsFormOpen(true)}
          className="flex items-center space-x-2 mt-4 lg:mt-0"
        >
          <Plus size={20} />
          <span>Add Activity</span>
        </Button>
      </motion.div>

      {/* Stats */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-primary-100 rounded-xl mr-4">
              <Target className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Activities</p>
              <p className="text-2xl font-bold text-gray-900">{activities.length}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-secondary-100 rounded-xl mr-4">
              <Clock className="w-6 h-6 text-secondary-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Hours</p>
              <p className="text-2xl font-bold text-gray-900">{totalHours.toLocaleString()}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-accent-100 rounded-xl mr-4">
              <Award className="w-6 h-6 text-accent-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Leadership Roles</p>
              <p className="text-2xl font-bold text-gray-900">{leadershipRoles}</p>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <Card>
          <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search activities..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Filter size={20} className="text-gray-400" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value as ActivityCategory | 'all')}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Activities Grid */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-6"
      >
        {filteredActivities.map((activity, index) => (
          <motion.div
            key={activity.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * index }}
          >
            <ActivityCard
              activity={activity}
              onEdit={handleEditActivity}
              onDelete={handleDeleteActivity}
            />
          </motion.div>
        ))}
      </motion.div>

      {filteredActivities.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Target className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm || selectedCategory !== 'all' ? 'No activities found' : 'No activities yet'}
          </h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || selectedCategory !== 'all'
              ? 'Try adjusting your search or filter criteria'
              : 'Start building your profile by adding your first activity'
            }
          </p>
          {!searchTerm && selectedCategory === 'all' && (
            <Button onClick={() => setIsFormOpen(true)}>
              Add Your First Activity
            </Button>
          )}
        </motion.div>
      )}

      {/* Activity Form Modal */}
      <ActivityForm
        activity={editingActivity}
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingActivity(undefined);
        }}
        onSave={handleSaveActivity}
      />
    </div>
  );
};